import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import STLViewer from '../components/STLViewer';
import Modal from '../components/Modal';
import { ArrowLeft, Upload, Download, Trash2, Edit3, Plus, Printer, FileCode, Package, Eye, ChevronDown, ChevronUp, Check } from 'lucide-react';

export default function ModelDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [model, setModel] = useState(null);
  const [parts, setParts] = useState([]);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [showUpload, setShowUpload] = useState(false);
  const [showGcodeUpload, setShowGcodeUpload] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showQueueAdd, setShowQueueAdd] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [queueQty, setQueueQty] = useState(1);
  const [queueSelectedParts, setQueueSelectedParts] = useState({});
  const [gcodeForm, setGcodeForm] = useState({ machine_id: '', notes: '' });
  const [expandedParts, setExpandedParts] = useState({});
  const [editingSubPart, setEditingSubPart] = useState(null);
  const [editingSubPartName, setEditingSubPartName] = useState('');
  const fileInputRef = useRef(null);
  const gcodeInputRef = useRef(null);

  const loadModel = useCallback(() => {
    api.get(`/models/${id}`).then(r => {
      setModel(r.data.model);
      setParts(r.data.parts);
      setTags(r.data.tags);
      if (r.data.parts.length > 0) {
        setSelectedPart(prev => {
          if (prev) {
            const updated = r.data.parts.find(p => p.id === prev.id);
            return updated || r.data.parts[0];
          }
          return r.data.parts[0];
        });
      }
    });
    api.get('/tags').then(r => setAllTags(r.data.tags));
    api.get('/machines').then(r => setMachines(r.data.machines));
  }, [id]);

  useEffect(() => { loadModel(); }, [loadModel]);

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files.length) return;
    const formData = new FormData();
    for (const f of files) formData.append('files', f);
    await api.post(`/models/${id}/parts`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    loadModel();
    setShowUpload(false);
  };

  const handleGcodeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !gcodeForm.machine_id) return;
    const formData = new FormData();
    formData.append('file', file);
    formData.append('machine_id', gcodeForm.machine_id);
    formData.append('notes', gcodeForm.notes);
    await api.post(`/gcode/part/${selectedPart.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
    setShowGcodeUpload(false);
    setGcodeForm({ machine_id: '', notes: '' });
    loadModel();
  };

  const handleUpdateModel = async () => {
    await api.put(`/models/${id}`, {
      name: editForm.name,
      description: editForm.description,
      tags: editForm.tags
    });
    setShowEdit(false);
    loadModel();
  };

  const handleDeleteModel = async () => {
    if (!window.confirm('Delete this model and all its parts?')) return;
    await api.delete(`/models/${id}`);
    navigate('/models');
  };

  const handleUpdatePart = async (partId, data) => {
    await api.put(`/models/${id}/parts/${partId}`, data);
    loadModel();
  };

  const handleDeletePart = async (partId) => {
    if (!window.confirm('Delete this part?')) return;
    await api.delete(`/models/${id}/parts/${partId}`);
    if (selectedPart?.id === partId) setSelectedPart(null);
    loadModel();
  };

  const handleAddToQueue = async () => {
    const selectedIds = Object.entries(queueSelectedParts).filter(([, v]) => v).map(([k]) => parseInt(k));
    await api.post('/queue', {
      model_id: parseInt(id),
      target_quantity: queueQty,
      part_ids: selectedIds.length > 0 && selectedIds.length < parts.length ? selectedIds : undefined
    });
    setShowQueueAdd(false);
    loadModel();
  };

  const handleDeleteGcode = async (gcodeId) => {
    if (!window.confirm('Delete this G-code profile?')) return;
    await api.delete(`/gcode/${gcodeId}`);
    loadModel();
  };

  const handleToggleSubPart = async (partId, subPartId, enabled) => {
    await api.put(`/models/${id}/parts/${partId}/subparts/${subPartId}`, { is_enabled: enabled });
    loadModel();
  };

  const handleReparse = async (partId) => {
    await api.post(`/models/${id}/parts/${partId}/reparse`);
    loadModel();
  };

  const handleRenameSubPart = async (partId, subPartId, newName) => {
    if (!newName.trim()) return;
    await api.put(`/models/${id}/parts/${partId}/subparts/${subPartId}`, { name: newName.trim() });
    setEditingSubPart(null);
    loadModel();
  };

  const togglePart = (partId) => {
    setExpandedParts(prev => ({ ...prev, [partId]: !prev[partId] }));
  };

  const formatTime = (seconds) => {
    if (!seconds) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatSize = (bytes) => {
    if (!bytes) return '—';
    if (bytes > 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(1)} KB`;
  };

  if (!model) return <div style={{ color: 'var(--text-muted)', padding: '40px', textAlign: 'center' }}>Loading model...</div>;

  return (
    <div className="fade-in">
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '24px' }}>
        <button className="btn-icon" onClick={() => navigate('/models')}><ArrowLeft size={18} /></button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '24px' }}>{model.name}</h1>
          {model.description && <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginTop: '4px' }}>{model.description}</p>}
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn btn-secondary" onClick={() => { setEditForm({ name: model.name, description: model.description || '', tags: tags.map(t => t.id) }); setShowEdit(true); }}>
            <Edit3 size={15} /> Edit
          </button>
          <button className="btn btn-primary" onClick={() => {
            const sel = {};
            parts.forEach(p => { sel[p.id] = true; });
            setQueueSelectedParts(sel);
            setShowQueueAdd(true);
          }}>
            <Printer size={15} /> Add to Queue
          </button>
          <button className="btn btn-danger" onClick={handleDeleteModel}>
            <Trash2 size={15} />
          </button>
        </div>
      </div>

      {/* Tags */}
      {tags.length > 0 && (
        <div style={{ display: 'flex', gap: '6px', marginBottom: '20px', flexWrap: 'wrap' }}>
          {tags.map(t => <span key={t.id} className="tag" style={{ background: t.color + '22', color: t.color }}>{t.name}</span>)}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: '24px' }}>
        {/* 3D Viewer / Preview */}
        <div>
          {selectedPart && selectedPart.file_type === 'stl' ? (
            <div className="viewer-container">
              <STLViewer url={`/api/models/${id}/parts/${selectedPart.id}/file`} height={400} />
            </div>
          ) : selectedPart && selectedPart.file_type === '3mf' && selectedPart.thumbnail_url ? (
            <div className="viewer-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative', overflow: 'hidden' }}>
              <img
                src={selectedPart.thumbnail_url}
                alt={`${selectedPart.name} preview`}
                style={{ maxWidth: '100%', maxHeight: '400px', objectFit: 'contain', borderRadius: 'var(--radius)' }}
              />
              {/* Color legend overlay */}
              {selectedPart.sub_parts?.length > 0 && (
                <div style={{
                  position: 'absolute', bottom: '12px', left: '12px',
                  background: 'rgba(6,8,15,0.85)', borderRadius: 'var(--radius-sm)',
                  padding: '8px 12px', backdropFilter: 'blur(8px)',
                  maxWidth: '260px'
                }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>
                    Objects
                  </div>
                  {selectedPart.sub_parts.filter(sp => sp.is_enabled).map(sp => (
                    <div key={sp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0' }}>
                      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: sp.color_hex, flexShrink: 0 }} />
                      <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{sp.name}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="viewer-container" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              {selectedPart ? (
                <div style={{ textAlign: 'center' }}>
                  <Package size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p>No preview available</p>
                  <p style={{ fontSize: '13px' }}>
                    {selectedPart.file_type === '3mf' ? 'Click Re-scan in the part details to extract a preview' : 'Select an STL part to view in 3D'}
                  </p>
                </div>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <Package size={48} style={{ marginBottom: '12px', opacity: 0.3 }} />
                  <p>Upload parts to view them in 3D</p>
                </div>
              )}
            </div>
          )}
          {selectedPart && (
            <div style={{ marginTop: '8px', fontSize: '13px', color: 'var(--text-secondary)', textAlign: 'center' }}>
              Viewing: <strong>{selectedPart.name}.{selectedPart.file_type}</strong>
              {selectedPart.file_type === 'stl' && ' — Click & drag to orbit, scroll to zoom'}
            </div>
          )}
        </div>

        {/* Parts list */}
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600' }}>Parts ({parts.length})</h3>
            <button className="btn btn-primary btn-sm" onClick={() => fileInputRef.current?.click()}>
              <Upload size={14} /> Upload Parts
            </button>
            <input ref={fileInputRef} type="file" multiple accept=".stl,.3mf" style={{ display: 'none' }} onChange={handleFileUpload} />
          </div>

          {parts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-muted)', border: '2px dashed var(--border)', borderRadius: 'var(--radius)' }}>
              <Upload size={32} style={{ marginBottom: '8px', opacity: 0.3 }} />
              <p>No parts yet</p>
              <p style={{ fontSize: '13px' }}>Upload STL or 3MF files</p>
            </div>
          ) : (
            <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
              {parts.map(part => (
                <div key={part.id} style={{ marginBottom: '8px' }}>
                  <div
                    className="part-row"
                    style={{
                      cursor: 'pointer',
                      borderColor: selectedPart?.id === part.id ? 'var(--accent)' : undefined,
                      background: selectedPart?.id === part.id ? 'var(--accent-dim)' : undefined
                    }}
                    onClick={() => setSelectedPart(part)}
                  >
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: part.file_type === 'stl' ? 'var(--accent)' : 'var(--warning)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '500', fontSize: '14px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{part.name}</div>
                      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                        {part.file_type.toUpperCase()} · {formatSize(part.file_size)} · Qty: {part.quantity_per_model}x
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                      {part.gcode_profiles?.length > 0 && (
                        <span className="badge badge-success" title="Has G-code">{part.gcode_profiles.length}</span>
                      )}
                      <button className="btn-icon" style={{ padding: '4px' }} onClick={(e) => { e.stopPropagation(); togglePart(part.id); }}>
                        {expandedParts[part.id] ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </div>
                  </div>

                  {/* Expanded part details */}
                  {expandedParts[part.id] && (
                    <div style={{ marginLeft: '16px', padding: '12px', background: 'var(--bg-secondary)', borderRadius: '0 0 var(--radius-sm) var(--radius-sm)', borderLeft: '2px solid var(--border)' }}>
                      <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setSelectedPart(part); setShowGcodeUpload(true); }}>
                          <Upload size={13} /> Upload G-code
                        </button>
                        <a href={`/api/models/${id}/parts/${part.id}/file`} className="btn btn-secondary btn-sm" download>
                          <Download size={13} /> Download
                        </a>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeletePart(part.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                        <label style={{ fontSize: '13px', color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>Qty per model:</label>
                        <input
                          type="number" min="1" value={part.quantity_per_model}
                          onChange={e => handleUpdatePart(part.id, { quantity_per_model: parseInt(e.target.value) || 1 })}
                          style={{ width: '80px' }}
                        />
                      </div>

                      {/* G-code profiles */}
                      {part.gcode_profiles?.length > 0 && (
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)', marginBottom: '6px' }}>G-code Profiles</div>
                          {part.gcode_profiles.map(gp => (
                            <div key={gp.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', marginBottom: '4px', fontSize: '13px' }}>
                              <FileCode size={14} color="var(--success)" />
                              <div style={{ flex: 1 }}>
                                <div style={{ fontWeight: '500' }}>{gp.machine_name}</div>
                                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>
                                  {formatTime(gp.print_time_seconds)} · {gp.filament_usage_grams ? `${gp.filament_usage_grams}g` : '—'} · v{gp.version}
                                </div>
                              </div>
                              <a href={`/api/gcode/${gp.id}/download`} className="btn-icon" style={{ padding: '4px' }} title="Download">
                                <Download size={14} />
                              </a>
                              <button className="btn-icon" style={{ padding: '4px' }} onClick={() => handleDeleteGcode(gp.id)} title="Delete">
                                <Trash2 size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Sub-parts for 3MF files */}
                      {part.file_type === '3mf' && (
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-secondary)' }}>
                              Objects in this 3MF ({part.sub_parts?.length || 0})
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => handleReparse(part.id)} title="Re-scan file">
                              &#8635; Re-scan
                            </button>
                          </div>

                          {!part.sub_parts || part.sub_parts.length === 0 ? (
                            <div style={{ fontSize: '13px', color: 'var(--text-muted)', padding: '8px' }}>
                              No objects parsed yet. Click Re-scan to extract from the 3MF file.
                            </div>
                          ) : (
                            <div style={{ maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                              {part.sub_parts.map(sp => (
                                <div key={sp.id} style={{
                                  display: 'flex', alignItems: 'center', gap: '10px',
                                  padding: '7px 10px',
                                  background: sp.is_enabled ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                                  borderRadius: 'var(--radius-sm)',
                                  border: '1px solid var(--border)',
                                  opacity: sp.is_enabled ? 1 : 0.45
                                }}>
                                  <div style={{
                                    width: '16px', height: '16px', borderRadius: '50%', flexShrink: 0,
                                    background: sp.color_hex, border: '2px solid rgba(255,255,255,0.15)'
                                  }} title={`Extruder ${sp.extruder}`} />
                                  <span style={{
                                    fontSize: '10px', fontWeight: '700', padding: '2px 5px',
                                    borderRadius: '4px', background: 'var(--bg-tertiary)',
                                    color: 'var(--text-muted)', flexShrink: 0
                                  }}>E{sp.extruder}</span>
                                  {editingSubPart === sp.id ? (
                                    <input
                                      autoFocus
                                      value={editingSubPartName}
                                      onChange={e => setEditingSubPartName(e.target.value)}
                                      onBlur={() => handleRenameSubPart(part.id, sp.id, editingSubPartName)}
                                      onKeyDown={e => {
                                        if (e.key === 'Enter') handleRenameSubPart(part.id, sp.id, editingSubPartName);
                                        if (e.key === 'Escape') setEditingSubPart(null);
                                      }}
                                      style={{ flex: 1, fontSize: '13px', padding: '2px 6px', background: 'var(--bg-tertiary)', border: '1px solid var(--accent)', borderRadius: '4px', color: 'var(--text-primary)', outline: 'none' }}
                                    />
                                  ) : (
                                    <span
                                      style={{ flex: 1, fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                                      title="Double-click to rename"
                                      onDoubleClick={() => { setEditingSubPart(sp.id); setEditingSubPartName(sp.name); }}
                                    >{sp.name}</span>
                                  )}
                                  <button
                                    onClick={() => handleToggleSubPart(part.id, sp.id, !sp.is_enabled)}
                                    style={{
                                      fontSize: '11px', padding: '3px 8px', borderRadius: '4px',
                                      background: sp.is_enabled ? 'var(--success-dim)' : 'var(--bg-tertiary)',
                                      color: sp.is_enabled ? 'var(--success)' : 'var(--text-muted)',
                                      border: `1px solid ${sp.is_enabled ? 'var(--success)' : 'var(--border)'}`,
                                      cursor: 'pointer'
                                    }}
                                  >
                                    {sp.is_enabled ? 'Active' : 'Ignored'}
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Edit Model Modal */}
      <Modal isOpen={showEdit} onClose={() => setShowEdit(false)} title="Edit Model"
        footer={<><button className="btn btn-secondary" onClick={() => setShowEdit(false)}>Cancel</button><button className="btn btn-primary" onClick={handleUpdateModel}>Save</button></>}>
        <div className="form-group">
          <label>Name</label>
          <input value={editForm.name || ''} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea value={editForm.description || ''} onChange={e => setEditForm({ ...editForm, description: e.target.value })} rows={3} />
        </div>
        <div className="form-group">
          <label>Tags</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {allTags.map(t => (
              <button key={t.id} type="button" className="tag"
                style={(editForm.tags || []).includes(t.id) ? { background: t.color + '33', color: t.color, border: `1px solid ${t.color}` } : { background: 'var(--bg-tertiary)', color: 'var(--text-muted)', border: '1px solid var(--border)' }}
                onClick={() => {
                  const tags = (editForm.tags || []).includes(t.id) ? editForm.tags.filter(x => x !== t.id) : [...(editForm.tags || []), t.id];
                  setEditForm({ ...editForm, tags });
                }}>
                {t.name}
              </button>
            ))}
          </div>
        </div>
      </Modal>

      {/* G-code Upload Modal */}
      <Modal isOpen={showGcodeUpload} onClose={() => setShowGcodeUpload(false)} title={`Upload G-code for ${selectedPart?.name || ''}`}
        footer={<button className="btn btn-secondary" onClick={() => setShowGcodeUpload(false)}>Close</button>}>
        <div className="form-group">
          <label>Machine</label>
          <select value={gcodeForm.machine_id} onChange={e => setGcodeForm({ ...gcodeForm, machine_id: e.target.value })}>
            <option value="">Select a machine...</option>
            {machines.map(m => <option key={m.id} value={m.id}>{m.brand} {m.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Notes (optional)</label>
          <input value={gcodeForm.notes} onChange={e => setGcodeForm({ ...gcodeForm, notes: e.target.value })} placeholder="e.g. 0.2mm layer, 15% infill" />
        </div>
        <div className="form-group">
          <label>G-code File</label>
          <div className="dropzone" onClick={() => gcodeInputRef.current?.click()}>
            <FileCode size={32} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <p>Click to select G-code file</p>
            <p style={{ fontSize: '12px', marginTop: '4px' }}>Supports .gcode, .bgcode, .g files</p>
          </div>
          <input ref={gcodeInputRef} type="file" accept=".gcode,.bgcode,.g,.gc" style={{ display: 'none' }} onChange={handleGcodeUpload} />
        </div>
        {!gcodeForm.machine_id && <p style={{ color: 'var(--warning)', fontSize: '13px' }}>Please select a machine first</p>}
      </Modal>

      {/* Add to Queue Modal */}
      <Modal isOpen={showQueueAdd} onClose={() => setShowQueueAdd(false)} title="Add to Print Queue"
        footer={<><button className="btn btn-secondary" onClick={() => setShowQueueAdd(false)}>Cancel</button><button className="btn btn-primary" onClick={handleAddToQueue} disabled={parts.length > 0 && !Object.values(queueSelectedParts).some(v => v)}>Add to Queue</button></>}>
        <p style={{ marginBottom: '16px', color: 'var(--text-secondary)', fontSize: '14px' }}>
          How many complete units of <strong>{model.name}</strong> do you need to print?
        </p>
        <div className="form-group">
          <label>Target Quantity</label>
          <input type="number" min="1" value={queueQty} onChange={e => setQueueQty(parseInt(e.target.value) || 1)} />
        </div>
        {parts.length > 0 && (
          <div style={{ background: 'var(--bg-tertiary)', borderRadius: 'var(--radius-sm)', padding: '14px', fontSize: '13px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
              <span style={{ fontWeight: '600' }}>Select parts to print:</span>
              <button
                style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '4px', background: 'var(--bg-secondary)', color: 'var(--text-muted)', border: '1px solid var(--border)', cursor: 'pointer' }}
                onClick={() => {
                  const allSelected = parts.every(p => queueSelectedParts[p.id]);
                  const next = {};
                  parts.forEach(p => { next[p.id] = !allSelected; });
                  setQueueSelectedParts(next);
                }}
              >
                {parts.every(p => queueSelectedParts[p.id]) ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            {parts.map(p => {
              const isSelected = !!queueSelectedParts[p.id];
              const enabled = p.sub_parts?.filter(sp => sp.is_enabled) || [];
              return (
                <div key={p.id} style={{ marginBottom: '4px' }}>
                  <div
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px',
                      borderRadius: 'var(--radius-sm)', cursor: 'pointer',
                      background: isSelected ? 'var(--accent-dim)' : 'transparent',
                      border: `1px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      opacity: isSelected ? 1 : 0.5,
                      transition: 'all 0.15s'
                    }}
                    onClick={() => setQueueSelectedParts(prev => ({ ...prev, [p.id]: !prev[p.id] }))}
                  >
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '4px', flexShrink: 0,
                      border: `2px solid ${isSelected ? 'var(--accent)' : 'var(--border)'}`,
                      background: isSelected ? 'var(--accent)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                      {isSelected && <Check size={12} color="#fff" />}
                    </div>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: p.file_type === 'stl' ? 'var(--accent)' : 'var(--warning)', flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                        {p.file_type.toUpperCase()} · Qty: {p.quantity_per_model}x
                        {enabled.length > 0 && ` · ${enabled.length} object${enabled.length !== 1 ? 's' : ''}`}
                      </div>
                    </div>
                    {isSelected && (
                      <span style={{ fontWeight: '600', whiteSpace: 'nowrap', color: 'var(--accent)' }}>
                        {p.quantity_per_model * queueQty}
                      </span>
                    )}
                  </div>
                  {/* Show sub-parts if selected and has them */}
                  {isSelected && enabled.length > 0 && (
                    <div style={{ marginLeft: '36px', paddingLeft: '8px', borderLeft: '2px solid var(--border)' }}>
                      {enabled.map(sp => (
                        <div key={sp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '2px 0', fontSize: '12px', color: 'var(--text-secondary)' }}>
                          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: sp.color_hex }} />
                          {sp.name}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
}
